/**
 * Grouper Table Sync Utilities
 * Functions for synchronizing grouper row additions/deletions to topic tables
 */

function getTableGrouperId(row: any): string {
  if (!row) return '';
  if (row.grouperId) return String(row.grouperId);
  if (row.id) return String(row.id);
  return '';
}

function getGrouperRowIdentifiers(row: any): string[] {
  if (!row) {
    return [];
  }
  if (row.grouperId) {
    return [String(row.grouperId)];
  }
  return [];
}

/**
 * Create an empty table row with placeholder cells for all columns
 */
function createEmptyTableRow(tableStructure: any, grouperId: string): any {
  if (!tableStructure?.columns) {
    return { grouperId, cells: [] };
  }

  const cells: any[] = [];

  tableStructure.columns.forEach((column: any) => {
    // Skip grouper reference columns (they're read-only and populated automatically)
    if (column.refTable === 'grouper') {
      return;
    }

    // Handle column groups (nested fields)
    if (column.type === 'group' && column.fields) {
      column.fields.forEach((field: any) => {
        if (field.refTable === 'grouper') {
          return;
        }
        cells.push({
          id: field.id,
          value: '',
          sourceInfo: {},
          reviewInfo: {}
        });
      });
    } else {
      cells.push({
        id: column.id,
        value: '',
        sourceInfo: {},
        reviewInfo: {}
      });
    }
  });

  return {
    grouperId: grouperId,
    cells
  };
}

function matchesGrouperRow(tableRow: any, grouperRow: any): boolean {
  if (!tableRow || !grouperRow) return false;
  const tableGrouperId = getTableGrouperId(tableRow);
  if (!tableGrouperId) return false;
  const identifiers = getGrouperRowIdentifiers(grouperRow);
  return identifiers.includes(tableGrouperId);
}

/**
 * Sync a single grouper row change to all topic tables
 */
export function syncGrouperRowToTables(
  topicId: string,
  grouperRowId: string | any,
  action: 'add' | 'delete',
  studyExtract: any,
  projectStructure: any
): any {
  if (!studyExtract || !projectStructure) {
    logWarning('[grouper-table-sync] Missing studyExtract or projectStructure', "grouper-table-sync", undefined);
    return studyExtract;
  }

  const topic = studyExtract.topics?.find((t: any) => t.id === topicId);
  if (!topic) {
    logWarning(`[grouper-table-sync] Topic not found: ${topicId}`, "grouper-table-sync", undefined);
    return studyExtract;
  }

  const topicStructure = projectStructure.topics?.find((t: any) => t.id === topicId);
  if (!topicStructure) {
    logWarning(`[grouper-table-sync] Topic structure not found: ${topicId}`, "grouper-table-sync", undefined);
    return studyExtract;
  }

  const tables = topicStructure.tables || [];
  if (tables.length === 0) {
    return studyExtract;
  }

  if (!Array.isArray(topic.grouper)) {
    logWarning(`[grouper-table-sync] Topic "${topicId}" has invalid grouper data`, "grouper-table-sync", undefined);
    return studyExtract;
  }

  let grouperRow: any = null;
  let grouperId: string = '';

  if (typeof grouperRowId === 'string') {
    grouperId = grouperRowId;
    grouperRow = topic.grouper.find((row: any) => {
      const identifiers = getGrouperRowIdentifiers(row);
      return identifiers.includes(grouperId);
    }) || null;
  } else {
    grouperRow = grouperRowId;
    grouperId = getGrouperRowIdentifiers(grouperRow)[0] || '';
  }

  if (!grouperId) {
    logWarning('[grouper-table-sync] Unable to resolve grouper row identifier for sync', "grouper-table-sync", undefined);
    return studyExtract;
  }

  if (!topic.tables) {
    topic.tables = [];
  }

  tables.forEach((tableStructure: any) => {
    const tableId = tableStructure.id;

    let tableData = topic.tables.find((t: any) => t.id === tableId);
    if (!tableData) {
      tableData = { id: tableId, rows: [] };
      topic.tables.push(tableData);
    }

    if (!Array.isArray(tableData.rows)) {
      tableData.rows = [];
    }

    if (action === 'add') {
      const existingRow = tableData.rows.find((row: any) => matchesGrouperRow(row, grouperRow) || getTableGrouperId(row) === grouperId);
      if (!existingRow) {
        const newRow = createEmptyTableRow(tableStructure, grouperId);
        tableData.rows.push(newRow);
      }
    } else if (action === 'delete') {
      const initialLength = tableData.rows.length;
      tableData.rows = tableData.rows.filter((row: any) => !matchesGrouperRow(row, grouperRow) && getTableGrouperId(row) !== grouperId);
      const removedCount = initialLength - tableData.rows.length;
    }
  });

  return studyExtract;
}

/**
 * Sync multiple grouper row changes to all topic tables (batch operation)
 */
export function syncGrouperRowsToTables(
  topicId: string,
  grouperRowIds: (string | any)[],
  action: 'add' | 'delete',
  studyExtract: any,
  projectStructure: any
): any {
  if (!grouperRowIds || grouperRowIds.length === 0) {
    return studyExtract;
  }

  grouperRowIds.forEach((grouperRowId) => {
    syncGrouperRowToTables(topicId, grouperRowId, action, studyExtract, projectStructure);
  });

  return studyExtract;
}

